import { Peer } from "peerjs";

export const SESSION_KEY = "headsup_session";
export const saveSession = (s) => localStorage.setItem(SESSION_KEY, JSON.stringify(s));
export const loadSession = () => { try { return JSON.parse(localStorage.getItem(SESSION_KEY)); } catch { return null; } };
export const clearSession = () => localStorage.removeItem(SESSION_KEY);

let myPeer = null;
let myConnectionToHost = null;
let hostConnections = [];
let isHost = false;
let localRoomState = null;

export const api = {
  getCurrentRoomState: () => localRoomState,

  updateRoomState: (newState) => {
    localRoomState = JSON.parse(JSON.stringify(newState));
    if (isHost) {
      hostConnections.forEach(conn => conn.send({ type: 'SYNC', room: localRoomState }));
    }
  },

  dispatchAction: (actionType, payload) => {
    return new Promise((resolve) => {
      if (isHost) {
        const nextState = JSON.parse(JSON.stringify(localRoomState));

        // --- INICJALIZACJA GRY TUROWEJ ---
        if (actionType === 'START_GAME') {
          nextState.status = "playing";
          nextState.server_now = Date.now() / 1000;
          
          const votedSetIds = Object.keys(nextState.votes || {});
          const allCards = nextState.sets.filter(s => votedSetIds.includes(s.id)).flatMap(s => s.cards || []);
          
          nextState.deck = [...allCards].sort(() => Math.random() - 0.5);
          if (nextState.deck.length === 0) nextState.deck = [{ text: "Brak kart!", description: "" }];
          
          nextState.active_turn_index = 0;
          nextState.turn_start_time = Date.now() / 1000;
          nextState.card_index = 0;
          nextState.confirm_votes = [];
          
          // Resetowanie statystyk (punkty i wykonane ruchy)
          nextState.players.forEach((p) => { 
              p.score = 0; 
              p.moves = 0;
          });
        }

        // SPRAWDZANIE WARUNKÓW WYGRANEJ (Zależnie od trybu)
        const checkWin = () => {
            const maxScore = Math.max(...nextState.players.map(p => p.score || 0));
            if (nextState.mode === "fcfs" && maxScore >= 1) return true;
            if (nextState.mode === "podium" && maxScore >= 3) return true;
            return false;
        };

        // ZAPĘTLANIE KART (Gdy zgramy wszystkie z talii)
        const advanceCard = () => {
            nextState.card_index += 1;
            nextState.confirm_votes = [];
            if (nextState.card_index >= nextState.deck.length) {
                nextState.deck = [...nextState.deck].sort(() => Math.random() - 0.5);
                nextState.card_index = 0;
            }
        };

        // ZLICZANIE RUCHÓW DO TIE-BREAKERA W TRYBIE PODIUM
        const incrementMoves = () => {
            if (nextState.players[nextState.active_turn_index]) {
                nextState.players[nextState.active_turn_index].moves = (nextState.players[nextState.active_turn_index].moves || 0) + 1;
            }
        };

        // --- WŁASNORĘCZNE WPISANIE (Insta-punkt) ---
        if (actionType === 'GUESS_CORRECT') {
          incrementMoves();
          if (nextState.players[nextState.active_turn_index]) {
             nextState.players[nextState.active_turn_index].score += 1;
          }
          if (checkWin()) {
             nextState.status = "ended";
          } else {
             advanceCard();
          }
        }

        // --- POMINIĘCIE KARTY ---
        if (actionType === 'SKIP') {
          incrementMoves();
          advanceCard();
        }

        // --- GŁOSOWANIE OBSERWATORÓW (75%) ---
        if (actionType === 'VOTE_CORRECT') {
          const { voterId } = payload;
          if (!nextState.confirm_votes) nextState.confirm_votes = [];
          
          if (!nextState.confirm_votes.includes(voterId)) {
             nextState.confirm_votes.push(voterId);
          }
          
          const otherPlayersCount = nextState.players.length - 1;
          const required = Math.max(1, Math.ceil(otherPlayersCount * 0.75));
          
          if (nextState.confirm_votes.length >= required) {
             incrementMoves();
             if (nextState.players[nextState.active_turn_index]) {
                nextState.players[nextState.active_turn_index].score += 1;
             }
             if (checkWin()) {
                nextState.status = "ended";
             } else {
                advanceCard();
             }
          }
        }

        // --- ZMIANA TURY ---
        if (actionType === 'NEXT_TURN') {
          // Modulo (%) sprawia, że po ostatnim graczu, tura wraca do 1 gracza (Karuzela!)
          nextState.active_turn_index = (nextState.active_turn_index + 1) % nextState.players.length;
          nextState.turn_start_time = Date.now() / 1000;
          advanceCard();
        }

        if (actionType === 'END_GAME') nextState.status = "ended";
        
        if (actionType === 'RESTART_GAME') {
            nextState.status = "lobby";
            nextState.players.forEach(p => { p.score = 0; p.moves = 0; });
            nextState.votes = {}; 
        }
        
        if (actionType === 'CONFIG') Object.assign(nextState, payload);
        
        if (actionType === 'ADD_SET') {
          if (!nextState.sets) nextState.sets = [];
          if (!nextState.sets.find(s => s.id === payload.id)) nextState.sets.push(payload);
        }

        if (actionType === 'VOTE') {
          const { setId, playerId } = payload;
          if (!nextState.votes) nextState.votes = {};
          const currentVoters = nextState.votes[setId] || [];
          if (currentVoters.includes(playerId)) {
            nextState.votes[setId] = currentVoters.filter(id => id !== playerId);
          } else {
            nextState.votes[setId] = [...currentVoters, playerId];
          }
        }

        localRoomState = nextState;
        api.updateRoomState(localRoomState);
        resolve({ room: localRoomState });

      } else if (myConnectionToHost) {
        myConnectionToHost.send({ type: 'ACTION', action: actionType, payload });
        resolve({ room: localRoomState }); 
      }
    });
  },

  createRoom: async (player_name) => {
    return new Promise((resolve, reject) => {
      isHost = true; hostConnections = [];
      if (myPeer) { myPeer.destroy(); }
      const code = Math.random().toString(36).substring(2, 6).toUpperCase();
      myPeer = new Peer(`czolko-${code}`);
      myPeer.on('open', (id) => {
        const playerId = "host-" + Date.now();
        localRoomState = {
          code: code, host_id: playerId, status: "lobby", 
          players: [{ id: playerId, name: player_name || "Host", score: 0, moves: 0, color: "#FFD24D" }],
          sets: [], votes: {}, mode: "fcfs", turn_seconds: 60, infinite: false
        };
        myPeer.on('connection', (conn) => {
          hostConnections.push(conn);
          conn.on('data', (data) => {
            if (data.type === 'JOIN') {
              localRoomState.players.push({ id: data.playerId, name: data.playerName, score: 0, moves: 0, color: "#00FFFF" });
              api.updateRoomState(localRoomState);
            }
            if (data.type === 'ACTION') api.dispatchAction(data.action, data.payload);
          });
        });
        resolve({ room: localRoomState, player_id: playerId });
      });
      myPeer.on('error', (err) => reject(err));
    });
  },

  joinRoom: async (code, player_name) => {
    return new Promise((resolve, reject) => {
      isHost = false; hostConnections = [];
      if (myPeer) { myPeer.destroy(); }
      myPeer = new Peer();
      myPeer.on('open', () => {
        const playerId = "client-" + Date.now();
        myConnectionToHost = myPeer.connect(`czolko-${code.toUpperCase()}`);
        myConnectionToHost.on('open', () => myConnectionToHost.send({ type: 'JOIN', playerId, playerName: player_name }));
        myConnectionToHost.on('data', (data) => {
          if (data.type === 'SYNC') {
            localRoomState = data.room;
            resolve({ room: localRoomState, player_id: playerId });
          }
        });
      });
      myPeer.on('error', (err) => reject(err));
    });
  },

  start: (code, player_id) => api.dispatchAction('START_GAME', { player_id }),
  end: (code, player_id) => api.dispatchAction('END_GAME', { player_id }),
  restart: (code, player_id) => api.dispatchAction('RESTART_GAME', { player_id }),
  config: (code, player_id, data) => api.dispatchAction('CONFIG', data),
  addSet: (code, name, cards) => api.dispatchAction('ADD_SET', { id: name, name: name, cards: cards }),
  vote: (code, player_id, data) => api.dispatchAction('VOTE', { setId: data, playerId: player_id }),
  
  guess: (code, player_id) => api.dispatchAction('GUESS_CORRECT', { playerId: player_id }),
  confirm: (code, player_id) => api.dispatchAction('VOTE_CORRECT', { voterId: player_id }),
  skip: (code, player_id) => api.dispatchAction('SKIP', { playerId: player_id }),
  question: (code, player_id) => api.dispatchAction('NEXT_TURN', { playerId: player_id }),
  
  leave: async () => { clearSession(); isHost = false; hostConnections = []; if (myPeer) { myPeer.destroy(); myPeer = null; } myConnectionToHost = null; },
  getRoom: async () => ({ room: localRoomState })
};